/**
 * Authoritative Daily Challenge day/reset math, entirely in
 * `America/New_York` - never the player's device timezone, and never
 * approximated as a fixed UTC offset (DST shifts it by an hour twice a
 * year). Pure and framework-free so it runs identically in the browser
 * and in a Netlify Function (imported by relative path from
 * `netlify/functions`, same convention as `src/online/types.ts`), and is
 * fully unit-testable without mocking a timezone database - it relies
 * only on the JS engine's built-in `Intl`/IANA tzdata, which both
 * Node (Netlify Functions) and every real browser ship.
 */

const NY_TIME_ZONE = "America/New_York";

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** `{ year, month (1-12), day }` of `utcMs` as observed in `America/New_York`. */
function nyCalendarParts(utcMs: number): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: NY_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(utcMs));
  const map = Object.fromEntries(parts.filter((p) => p.type !== "literal").map((p) => [p.type, p.value]));
  return { year: Number(map.year), month: Number(map.month), day: Number(map.day) };
}

/** The NY calendar date `utcMs` falls on, as `YYYY-MM-DD` - the Daily Challenge `challenge_id`. */
export function nyDateString(utcMs: number): string {
  const { year, month, day } = nyCalendarParts(utcMs);
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * The `America/New_York` UTC offset (in ms, to ADD to a UTC instant to
 * get NY wall-clock time) in effect at `utcMs` - negative always (NY is
 * always behind UTC), `-5h` on EST, `-4h` on EDT. Computed by formatting
 * `utcMs` into NY wall-clock fields and comparing that wall-clock value
 * (reinterpreted as if it were itself a UTC instant) against the real UTC
 * instant - the difference between the two IS the zone's current offset.
 * The same technique `date-fns-tz`'s `zonedTimeToUtc` uses; needs no
 * hardcoded DST transition dates/rules, since `Intl` already carries the
 * full IANA tzdata (correct for 2026's transitions and every year after).
 */
function nyOffsetMsAt(utcMs: number): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: NY_TIME_ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcMs));
  const map = Object.fromEntries(parts.filter((p) => p.type !== "literal").map((p) => [p.type, p.value]));
  // Defensive: some ICU builds have historically emitted "24" for
  // midnight with certain hour options instead of "00" - never let that
  // silently roll this into the wrong calendar day.
  const hour = Number(map.hour) === 24 ? 0 : Number(map.hour);
  const wallClockAsUtcMs = Date.UTC(Number(map.year), Number(map.month) - 1, Number(map.day), hour, Number(map.minute), Number(map.second));
  return wallClockAsUtcMs - utcMs;
}

/** The real UTC instant of `00:00:00` NY-local time on the given `YYYY-MM-DD` NY calendar date. Correct across a DST transition day (23h or 25h from the previous/next midnight) because the offset is resolved from that exact midnight, not assumed constant. */
export function nyDateStringToUtcMidnightMs(dateString: string): number {
  const [year, month, day] = dateString.split("-").map(Number);
  const wallClockAsUtcMs = Date.UTC(year, month - 1, day, 0, 0, 0);
  // The offset barely varies within a single day (DST always flips at
  // 2 a.m. local, never at midnight), so resolving it at the midnight
  // instant itself is exact - no iteration/refinement needed.
  const offsetMs = nyOffsetMsAt(wallClockAsUtcMs);
  return wallClockAsUtcMs - offsetMs;
}

export interface ChallengeDay {
  /** The NY calendar date `nowMs` falls on - the authoritative Daily Challenge `challenge_id`. */
  challengeId: string;
  /** Echoes the instant this was resolved against - callers should surface it as "server time" rather than trusting the caller's own clock. */
  serverTimeMs: number;
  /** The real UTC instant of the next NY midnight after `nowMs` - when `challengeId` rolls over. */
  nextResetAtMs: number;
}

/**
 * The single source of truth for "what day is it, for Daily Challenge
 * purposes, right now" - every caller (challenge generation/lookup,
 * attempt reservation, the countdown UI) goes through this, never
 * `new Date()`/the player's device clock directly. `nowMs` should always
 * be the server's own clock (`Date.now()` inside a Netlify Function) for
 * anything authoritative - a Daily Challenge Netlify Function must never
 * trust a client-supplied date for the real challenge day (see
 * `netlify/functions/daily-attempt.ts`); it exists as a parameter purely
 * so this stays a pure, directly-testable function.
 */
export function getChallengeDay(nowMs: number): ChallengeDay {
  const challengeId = nyDateString(nowMs);
  const [year, month, day] = challengeId.split("-").map(Number);
  // Anchored at NY noon the next calendar day (not midnight) - guaranteed
  // clear of the 2 a.m. DST transition hour, so this always resolves to
  // the correct following calendar date even on a transition day, then
  // `nyDateStringToUtcMidnightMs` computes that date's own real midnight
  // instant precisely.
  const nextDayNoonGuessMs = Date.UTC(year, month - 1, day + 1, 16, 0, 0); // ~noon ET regardless of EST/EDT
  const nextChallengeId = nyDateString(nextDayNoonGuessMs);
  return { challengeId, serverTimeMs: nowMs, nextResetAtMs: nyDateStringToUtcMidnightMs(nextChallengeId) };
}
